import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { activeSubscriptionSelector, selectUserObject } from '@common-reducers/UserSelectors';
import { selectConnectionKind } from '@common-reducers/ConnectedEnginesSlice';
import { useGetFirstWeekChecklistQuery } from '@common-reducers/backend-api/backend-api';
import { usePatchUserPreferences } from '@common/hooks/use-patch-user-preferences';
import { usePingStatus } from '@common/components/whatsapp-tab-throttle-status/use-ping-status';
import { useNowTick } from '@common/hooks/use-now-tick';
import { selectDisplayEngines } from '../engines-management/engines-management.selectors';
import { conditionsFor, extensionAccount, messageDoneFrom, OnboardingPath, pendingGateway, setupNextAction } from './onboarding-logic';

export const PATH_KEY = 'bt-onboarding-path-v1';

/** The remembered path ("this computer" / "always on"); storage may be blocked. */
export function readStoredPath(): OnboardingPath | null {
  try {
    const v = window.localStorage.getItem(PATH_KEY);
    return v === 'local' || v === 'remote' ? v : null;
  } catch { return null; }
}

export function writeStoredPath(p: OnboardingPath | null): void {
  try {
    if (p === null) window.localStorage.removeItem(PATH_KEY);
    else window.localStorage.setItem(PATH_KEY, p);
  } catch { /* blocked */ }
}

/**
 * The live state every connect surface reads — the connection, the extension's ping, the plan,
 * the server's first-week checklist, the engines — plus `ready`: live state arrives a moment
 * after load (MQTT presence, sessions, the checklist), and until it has, every user looks "not
 * connected". Holding on `ready` is what keeps an already-connected user from a flash.
 */
export function useLiveConnectState() {
  const user = useAppSelector(selectUserObject);
  const activeSubscription = useAppSelector(activeSubscriptionSelector);
  const liveKind = useAppSelector(selectConnectionKind);
  const { hasExtension, status: ping } = usePingStatus();
  const { data: checklist, isLoading: checklistLoading, refetch: refetchChecklist } = useGetFirstWeekChecklistQuery(undefined, { skip: !user?._id });
  const sessionsLoaded = useAppSelector((state) => state.DBSessionReducer.itemsLoaded);
  const nowTick = useNowTick();
  const displayEngines = useAppSelector((state) => selectDisplayEngines(state, nowTick));

  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (settled) return undefined;
    if (liveKind !== 'none') { setSettled(true); return undefined; }
    const id = window.setTimeout(() => setSettled(true), 2500);
    return () => window.clearTimeout(id);
  }, [liveKind, settled]);
  const [giveUp, setGiveUp] = useState(false);
  useEffect(() => { const id = window.setTimeout(() => setGiveUp(true), 8000); return () => window.clearTimeout(id); }, []);

  return {
    user,
    activeSubscription,
    allowedEngines: activeSubscription?.getAllowedEngineCount() ?? 0,
    liveKind,
    hasExtension,
    ping,
    account: extensionAccount({ reportsUser: !!ping?.reportsSignedInUser, extensionUserId: ping?.signedInUserId ?? null, myUserId: user?._id ?? null }),
    checklist,
    refetchChecklist,
    displayEngines,
    ready: giveUp || (settled && !checklistLoading && (sessionsLoaded || !user?._id)),
  };
}

/** Where the user is in the setup, for surfaces outside the wizard (the sidebar progress, the
 *  calendar's one-time auto-open) — the same conditions as the wizard, so the step and the bar
 *  always match it; `done` is the first message having gone out. */
export function useSetupStatus() {
  const s = useLiveConnectState();
  const isPro = s.allowedEngines > 0;
  // Setup is complete once the first message went out (it needed a connection) — history, not
  // live presence: a set-up user whose WhatsApp Web is closed right now is not "unfinished".
  // Established accounts count as complete (messageDoneFrom).
  const complete = messageDoneFrom(s.checklist, false);
  const flow = conditionsFor({
    kind: s.liveKind,
    path: readStoredPath(),
    chooseAgain: false,
    hasExtension: s.hasExtension,
    account: s.account,
    canRemote: isPro,
    pendingRemote: isPro && !!pendingGateway(s.displayEngines, null),
    messageDone: complete,
  });
  return {
    // No checklist (still loading, or it failed) = no answer — show nothing, open nothing.
    ready: s.ready && !!s.checklist,
    progress: flow.progress,
    done: complete,
    /** The very next thing to do (null once done). */
    next: setupNextAction(flow),
    /** The account has seen /onboarding at least once (any device). */
    seen: !!s.user?.preferences?.onboardingSeenAt,
  };
}

/** /onboarding marks the account as having seen it (once) — the calendar stops auto-opening it. */
export function useMarkOnboardingSeen(): void {
  const user = useAppSelector(selectUserObject);
  const patchPreferences = usePatchUserPreferences();
  const marked = useRef(false);
  useEffect(() => {
    if (marked.current || !user?._id || user.preferences?.onboardingSeenAt) return;
    marked.current = true;
    patchPreferences(() => ({ onboardingSeenAt: new Date().toISOString() })).catch(() => { marked.current = false; });
  }, [user?._id, user?.preferences?.onboardingSeenAt]); // eslint-disable-line react-hooks/exhaustive-deps
}
